import { Grid, Box } from '@mui/material';
import classNames from 'classnames';
import { OptionCard } from 'shared/components/OptionsWithInput/OptionCard';
import { Option } from 'shared/components/OptionsWithInput/Option';
import React, {
  ComponentProps,
  FC,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useStyles } from './OptionsWithInput.styles';
import { CurrencyInput, CurrencyInputProps } from '../Form/CurrencyInput';

interface OptionsWithInputProps
  extends Omit<CurrencyInputProps, 'onSelect' | 'onBlur' | 'showUsdEquivalent'> {
  value: number | '';
  onSelect: (value: number) => void;
  onBlur?: (value: number) => void;
  options: Option[];
  isInputDisabled?: boolean;
  isInputHidden?: boolean;
  showUsdEquivalentInInput?: boolean;
  inputGrid?: ComponentProps<typeof Grid>;
  areOptionsDeactivated?: boolean;
  areOptionsDisabled?: boolean;
  inputSuffix?: string;
}

export const OptionsWithInput: FC<PropsWithChildren<OptionsWithInputProps>> = forwardRef(
  (
    {
      value,
      onChange,
      onSelect,
      onBlur = () => {},
      options,
      isInputDisabled = true,
      isInputHidden = false,
      showUsdEquivalentInInput = false,
      inputGrid = { xs: 6, md: 3 },
      areOptionsDeactivated = false,
      areOptionsDisabled = false,
      inputSuffix,
      ...rest
    },
    ref
  ) => {
    const { classes } = useStyles();
    const optionsValues = options.map((option) => option.value);

    const isOneOfOptionsValues = useCallback(
      (valueToCheck: number) => (valueToCheck ? optionsValues.includes(valueToCheck) : false),
      [optionsValues]
    );

    const [customValue, setCustomValue] = useState<number | string>(
      !value || isOneOfOptionsValues(+value) ? '' : value
    );

    const isInputActive = useMemo(
      () => customValue && customValue === value && !isOneOfOptionsValues(+customValue),
      [customValue, isOneOfOptionsValues, value]
    );

    const selectOption = useCallback(
      (newValue: number) => {
        if (newValue === value) return;

        onSelect(newValue);
        setCustomValue('');
      },
      [value, onSelect, setCustomValue]
    );

    const onBlurHandler = useCallback(() => {
      if (isOneOfOptionsValues(+customValue)) {
        setCustomValue('');
      }
      if (customValue === value) return;

      onBlur(+customValue);
    }, [isOneOfOptionsValues, value, customValue, onBlur]);

    const onChangeHandler = useCallback(
      (newCustomValue) => {
        if (Number.isNaN(newCustomValue) || newCustomValue === 0 || newCustomValue === undefined) {
          setCustomValue('');
          return;
        }

        setCustomValue(newCustomValue);
      },
      [setCustomValue]
    );

    return (
      <Box ref={ref}>
        <Box
          gap={2}
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(min(100%, 155px), 1fr))"
        >
          {options.map((option) => (
            <Grid item key={option.value}>
              <OptionCard
                {...option}
                disabled={areOptionsDisabled || option.disabled}
                onSelect={selectOption}
                isActive={!areOptionsDeactivated && option.value === value}
              />
            </Grid>
          ))}

          {!isInputHidden && (
            <Grid {...inputGrid} item>
              <CurrencyInput
                value={customValue}
                onChange={onChangeHandler}
                onBlur={onBlurHandler}
                placeholder="Custom"
                className={classNames(classes.input, {
                  [classes.active]: isInputActive,
                  [classes.disabled]: isInputDisabled,
                })}
                showUsdEquivalent={showUsdEquivalentInInput}
                suffix={inputSuffix}
                {...rest}
              />
            </Grid>
          )}
        </Box>
      </Box>
    );
  }
);
